"use client";

import { useSyncExternalStore } from "react";

/** A module-level store that outlives component mounts, read with useSyncExternalStore. */
export function createStore<T extends object>(initial: T) {
  let state = initial;
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const get = () => state;

  const set = (patch: Partial<T> | ((current: T) => Partial<T>)) => {
    const next = typeof patch === "function" ? patch(state) : patch;
    state = { ...state, ...next };
    listeners.forEach((listener) => listener());
  };

  const reset = () => set(initial);

  /** The whole state; it is replaced, never mutated, so the reference is stable between sets. */
  const useStore = () => useSyncExternalStore(subscribe, get, () => initial);

  return { get, set, reset, subscribe, useStore };
}
